import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { onAnimationEnd, yieldToMainThread } from '@theme/utilities';

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} PopularAddOnRefs
 * @property {HTMLElement[]} swatches
 * @property {HTMLElement[]} images
 * @property {HTMLElement} variantText
 * @property {HTMLElement} atcButton
 */

/**
 * PopularAddOnComponent
 *
 * Manages individual product add-on cards, handling variant swatches
 * and adding items to the cart.
 *
 * @extends {Component<PopularAddOnRefs>}
 */
class PopularAddOnComponent extends Component {
    /** @type {string | null} */
    #selectedVarId = null;

    /** @type {string | null} */
    #selectedColorTag = null;

    /** @type {string | null} */
    #selectedFeaturedMediaId = null;

    /** @type {number | undefined} */
    #resetTimeout;

    #abortController = new AbortController();

    connectedCallback() {
        super.connectedCallback();

        const swatches = this.refs.swatches ?? [];
        const firstSwatch = swatches.find((swatch) => swatch.dataset.available !== 'false');
        if (firstSwatch) {
            this.selectSwatch(firstSwatch);
        } else if (this.dataset.available === 'true' && this.dataset.selectedVariant) {
            this.#selectedVarId = this.dataset.selectedVariant;
        }

        const modal = this.querySelector('.na-modal--add-ons');
        modal?.addEventListener('dialog:open', this.#resetModalSlider, { signal: this.#abortController.signal });
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        clearTimeout(this.#resetTimeout);
        this.#abortController.abort();
    }

    #resetModalSlider = () => {
        this.#updatePopupGallery();
    };

    /**
     * Filters the popup gallery to show only images for the selected variant
     * and navigates to that variant's featured image.
     */
    #updatePopupGallery() {
        const slideshow = /** @type {any} */ (this.querySelector('.na-modal-add-ons__media slideshow-component'));
        if (!slideshow) return;

        const colorTag = this.#selectedColorTag;

        /**
         * @param {HTMLElement} el
         * @returns {boolean}
         */
        const shouldShow = (el) => {
            const tag = el.getAttribute('data-color-tag');
            return !colorTag || !tag || tag === colorTag;
        };

        // Show/hide slideshow-slide elements.
        // The slideshow's `slides` getter automatically excludes hidden slides.
        /** @type {NodeListOf<HTMLElement>} */
        const allSlides = slideshow.querySelectorAll('slideshow-slide');
        allSlides.forEach((slide) => {
            if (shouldShow(slide)) {
                slide.removeAttribute('hidden');
            } else {
                slide.setAttribute('hidden', '');
            }
        });

        // Show/hide thumbnail buttons (ref="thumbnails[]") by color tag.
        // The slideshow's `thumbnails` getter excludes hidden thumbnails automatically.
        /** @type {NodeListOf<HTMLElement>} */
        const thumbButtons = slideshow.querySelectorAll('[ref="thumbnails[]"][data-color-tag]');
        thumbButtons.forEach((btn) => {
            if (shouldShow(btn)) {
                btn.removeAttribute('hidden');
            } else {
                btn.setAttribute('hidden', '');
            }
        });

        // Show/hide dot list items by color tag.
        /** @type {NodeListOf<HTMLElement>} */
        const dotItems = slideshow.querySelectorAll('li[data-color-tag]');
        dotItems.forEach((li) => {
            if (shouldShow(li)) {
                li.removeAttribute('hidden');
            } else {
                li.setAttribute('hidden', '');
            }
        });

        // Read the color→featured_media map rendered by Liquid (uses FIRST variant per color,
        // matching PDP behaviour). Falls back to the swatch attribute if the map is absent.
        const mapScript = this.querySelector('script[data-popup-color-media-map]');
        /** @type {Record<string, number>} */
        let popupColorMap = {};
        if (mapScript?.textContent) {
            try {
                popupColorMap = JSON.parse(mapScript.textContent);
            } catch {}
        }
        const mapId = colorTag && popupColorMap[colorTag] ? String(popupColorMap[colorTag]) : null;
        const featuredMediaId = mapId ?? this.#selectedFeaturedMediaId;

        // Move the featured slide to the front of the visible slides in the DOM so that
        // this.slides[0] === featured and this.thumbnails[0] === its thumbnail. This keeps
        // the slideshow's internal index logic (which is DOM-order based) perfectly in sync
        // with the thumbnail strip — no CSS order tricks needed.
        // The MutationObserver in Component fires as a microtask after the DOM change, so
        // this.refs.slides / this.refs.thumbnails are both updated before the RAF runs.
        if (featuredMediaId) {
            const scroller = /** @type {HTMLElement | null} */ (slideshow.querySelector('slideshow-slides'));
            if (scroller) {
                const featuredSlide = /** @type {HTMLElement | null} */ (
                    scroller.querySelector(`slideshow-slide[slide-id="${featuredMediaId}"]:not([hidden])`)
                );
                const firstVisibleSlide = /** @type {HTMLElement | null} */ (
                    scroller.querySelector('slideshow-slide:not([hidden])')
                );
                if (featuredSlide && firstVisibleSlide && featuredSlide !== firstVisibleSlide) {
                    scroller.insertBefore(featuredSlide, firstVisibleSlide);
                }
            }

            // Mirror the same reordering in the thumbnail strip so thumb[0] === featured thumb.
            // Thumbnail buttons carry id="thumbnail-{media.id}" (from slideshow-controls.liquid).
            const thumbContainer = /** @type {HTMLElement | null} */ (
                slideshow.querySelector('.slideshow-controls__thumbnails')
            );
            if (thumbContainer) {
                const featuredThumb = /** @type {HTMLElement | null} */ (
                    thumbContainer.querySelector(`#thumbnail-${featuredMediaId}:not([hidden])`)
                );
                const firstVisibleThumb = /** @type {HTMLElement | null} */ (
                    thumbContainer.querySelector('[ref="thumbnails[]"]:not([hidden])')
                );
                if (featuredThumb && firstVisibleThumb && featuredThumb !== firstVisibleThumb) {
                    thumbContainer.insertBefore(featuredThumb, firstVisibleThumb);
                }
            }
        }

        // Double-RAF: first RAF lets the MutationObserver update refs after our DOM moves;
        // second RAF fires after the browser recalculates layout so scroller positions are correct.
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                slideshow.select(0, undefined, { animate: false, force: true });
            });
        });
    }

    /**
     * Handles clicking on a product swatch.
     * @param {Event} event
     */
    handleSwatchClick(event) {
        event.preventDefault();
        const swatch = /** @type {HTMLElement} */ (event.target);
        this.selectSwatch(swatch);
    }

    /**
     * Synchronizes the UI when a swatch is selected.
     * @param {HTMLElement} swatch
     */
    selectSwatch(swatch) {
        const { index, name, varid: varId } = swatch.dataset;
        if (!index || !varId) return;

        // Store color tag and featured media ID for popup gallery filtering
        this.#selectedColorTag = swatch.dataset.colorTag ?? null;
        this.#selectedFeaturedMediaId = swatch.dataset.featuredMediaId ?? null;

        // 1. Update Images
        const images = this.refs.images || [];
        images.forEach((img) => {
            img.style.display = 'none';
        });

        const activeImg = images.find((img) => img.hasAttribute(`data-index-${index}`));
        if (activeImg) {
            activeImg.style.display = '';
        }

        // 2. Update Swatch Selection State
        const swatches = this.refs.swatches || [];
        swatches.forEach((s) => s.classList.remove('selectedColor'));
        swatch.classList.add('selectedColor');

        // 3. Update Variant Text
        if (this.refs.variantText) {
            this.refs.variantText.textContent = name || '';
        }

        // 4. Update Internal State
        this.#selectedVarId = varId;
        this.setAttribute('data-selected-variant', varId);
        this.setAttribute('data-option-value', name || '');

        // Enable Add to Cart button if it was disabled
        if (this.refs.atcButton) {
            this.refs.atcButton.classList.remove('disabled');
        }

        // If the popup is already open, update the gallery to match the new variant
        if (this.querySelector('dialog[open]')) {
            this.#updatePopupGallery();
        }
    }

    /**
     * Handles adding the selected variant to the cart.
     */
    async handleAddToCartClick() {
        if (!this.#selectedVarId) {
            alert('Please select a color first');
            return;
        }

        await this.#animateAddToCart();

        try {
            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: this.#selectedVarId, quantity: 1 }),
            });

            if (!response.ok) {
                throw new Error(`Cart add failed: ${response.status}`);
            }

            const data = await response.json();

            // Dispatch global CartAddEvent to trigger the theme's cart drawer/notification
            document.dispatchEvent(
                new CartAddEvent({
                    item: data,
                    source: 'popular-add-ons',
                })
            );
        } catch (error) {
            console.error('Error adding to cart:', error);
        }
    }

    /**
     * Triggers the checkmark burst and fly-to-cart animations on click.
     */
    async #animateAddToCart() {
        const { atcButton } = this.refs;
        if (!atcButton) return;

        if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

        atcButton.dataset.added = 'true';

        // Audit note: intentional cross-component lookup (do not flag) — the cart icon lives in the
        // header, outside this component's subtree, so this.refs/this.querySelector cannot reach it.
        const cartIcon = document.querySelector('.header-actions__cart-icon');
        const visibleImageEl = /** @type {HTMLImageElement | null} */ (
            (this.refs.images || []).find((el) => el.style.display !== 'none')?.querySelector('img')
        );

        if (cartIcon && visibleImageEl?.src) {
            const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
            flyToCartElement.classList.add('fly-to-cart--sticky');
            flyToCartElement.style.setProperty('background-image', `url(${visibleImageEl.src})`);
            flyToCartElement.style.setProperty('--start-opacity', '0');
            flyToCartElement.source = visibleImageEl;
            flyToCartElement.destination = cartIcon;
            document.body.appendChild(flyToCartElement);
        }

        await yieldToMainThread();
        await onAnimationEnd(atcButton);

        this.#resetTimeout = setTimeout(() => {
            atcButton.removeAttribute('data-added');
        }, 800);
    }
}

if (!customElements.get('popular-add-on-component')) {
    customElements.define('popular-add-on-component', PopularAddOnComponent);
}

// "Load more / Load less accessories" — toggle hidden add-on products.
// Audit note: intentionally a module-scope page-level delegated handler (do not flag) — the trigger
// lives in the block (.na-product__add-ons-cta) outside any <popular-add-on-component> subtree, and
// ES modules execute once, so this registers a single listener for the page lifetime. No cleanup needed.
document.addEventListener('click', (event) => {
    const btn = event.target instanceof HTMLElement ? event.target.closest('.js-na-load-more-addons') : null;
    if (!(btn instanceof HTMLElement)) return;

    const targetId = btn.dataset.targetId;
    if (!targetId) return;

    const container = document.getElementById(targetId);
    if (!container) return;

    const isExpanded = !container.hasAttribute('hidden');
    const label = btn.querySelector('.na-link__text') ?? btn;

    if (isExpanded) {
        container.setAttribute('hidden', '');
        label.textContent = btn.dataset.moreText ?? label.textContent;
    } else {
        container.removeAttribute('hidden');
        label.textContent = btn.dataset.lessText ?? label.textContent;
    }
});
